
import { LinkedUser } from '@/hooks/use-broker';
import { Application } from '@/types/database.types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, FileEdit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface LinkedUsersApplicationsProps {
  linkedUsers: LinkedUser[];
  applications: Application[];
  isLoading: boolean;
}

export const LinkedUsersApplications = ({ 
  linkedUsers, 
  applications, 
  isLoading 
}: LinkedUsersApplicationsProps) => {
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (applications.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Applications Yet</h3>
          <p className="text-muted-foreground text-center max-w-md">
            Your linked clients haven't submitted any applications yet. 
            Encourage them to start their application process.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {applications.map((app) => {
        // Find the user associated with this application
        const linkedUser = linkedUsers.find(user => user.user_id === app.user_id);
        
        return (
          <Card key={app.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">
                    {app.company_legal_name || "Untitled Application"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Client: {linkedUser 
                      ? `${linkedUser.profile.first_name} ${linkedUser.profile.last_name}` 
                      : 'Unknown Client'}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm text-muted-foreground">
                      Status: <span className={
                        app.status === 'submitted' 
                        ? 'text-primary font-medium' 
                        : 'text-amber-500 font-medium'
                      }>
                        {(app.status || 'draft').toUpperCase()}
                      </span>
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Progress: {app.progress || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {app.created_at && (
                      <>Created {formatDistanceToNow(new Date(app.created_at), { addSuffix: true })}</>
                    )}
                  </p>
                </div>
                
                <div className="h-2.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500 ease-in-out" 
                    style={{ width: `${app.progress || 0}%` }}
                  ></div>
                </div>
                
                <Button
                  variant="outline"
                  className="ml-4"
                  onClick={() => navigate(`/application?id=${app.id}&userId=${app.user_id}`)}
                >
                  {app.status === 'submitted' ? (
                    <FileText className="h-4 w-4 mr-2" />
                  ) : (
                    <FileEdit className="h-4 w-4 mr-2" />
                  )}
                  {app.status === 'submitted' ? 'View' : 'Edit'}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
